'use client'

import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { usePathname, useParams } from 'next/navigation'
import { useSelector, useDispatch } from 'react-redux'
import Link from 'next/link'
import clsx from 'clsx'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import NonMemberModal from '@/components/Shared/NonMemberModal'
import MessageAfterLogin from '@/components/Shared/MessageAfterLogin'
import { useNavigationState } from '@/hooks/useNavigationState'
import { useAuth } from '@/hooks/useAuth'
import CompanyCommonInfo from '@/components/Shared/Company/CompanyCommonInfo/index'
import CustomTabs from '@/components/Shared/CustomTabs'
import { AppDispatch } from '@/store'
import { fetchCompanyCommonInfo, selectCompanyCommonInfo } from '@/store/companies/slice'
import { Company } from '@/types/company'
import styles from './styles.module.scss'
import { useRoleModelBannerVisibility } from '@/hooks/useRoleModelBannerVisibility'
import { SESSION_STORAGE_KEYS } from '@/constants/storageKeys'

interface CompanyTab {
  label: string
  route: string
}

export default function CompanyLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const params = useParams()

  const isPreview = pathname?.startsWith('/preview/') || false
  let companyId: string

  if (isPreview) {
    // プレビューページはURLからIDを直接抽出
    const match = pathname?.match(/\/preview\/companies\/([^/]+)/)
    companyId = match ? match[1] : ''
  } else {
    companyId = (params?.company as string) || ''
  }

  const dispatch = useDispatch<AppDispatch>()
  const companyCommonInfo = useSelector(selectCompanyCommonInfo)

  const [activeTab, setActiveTab] = useState(0)
  const [isDisabled, setIsDisabled] = useState(false)
  const { beforePath, removeBeforePath } = useNavigationState()
  const { getMemberId } = useAuth()
  const showRoleModelFinderBanner = useRoleModelBannerVisibility(companyId)

  const allTabs = useMemo<CompanyTab[]>(
    () => [
      { label: 'ホーム', route: '' },
      { label: 'ブック', route: 'books' },
      { label: 'コンテンツ', route: 'contents' },
      { label: 'タレント', route: 'talents' },
      { label: '動画', route: 'movies' },
      { label: 'アルバム', route: 'albums' },
      { label: 'メディア/プレス', route: 'media-releases' },
      { label: '採用情報', route: 'recruitments' },
      { label: '会社概要', route: 'company-info' },
    ],
    [],
  )

  const filteredTabs = useMemo(() => {
    if (!companyCommonInfo) return []
    const filtered = allTabs.filter(
      (tab) => tab.route === '' || companyCommonInfo.tabs.includes(tab.route),
    )
    return filtered
  }, [companyCommonInfo, allTabs])

  const isPreviewPath = useCallback((): boolean => {
    return pathname ? /^\/preview\//.test(pathname) : false
  }, [pathname])

  const isCompanyPreviewPath = useCallback((): boolean => {
    return pathname ? /^\/preview\/companies\//.test(pathname) : false
  }, [pathname])

  // パス変更時の会社情報取得
  useEffect(() => {
    if (!companyId) return

    // プレビューページの判定
    const isPreview = pathname?.startsWith('/preview/') || false

    // Redux actionをdispatch（thunk内で同一企業チェックを実行）
    dispatch(fetchCompanyCommonInfo({ companyId, isPreview }))
  }, [companyId, pathname, dispatch])

  // previewDisabledButton設定
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isDisabled =
        sessionStorage.getItem(SESSION_STORAGE_KEYS.PREVIEW_DISABLED_BUTTON) === 'true'
      setIsDisabled(isDisabled)
    }
  }, [])

  // Separate useEffect for handling beforePath
  useEffect(() => {
    if (beforePath) {
      removeBeforePath()
    }
  }, [beforePath, removeBeforePath])

  // タブ設定とアクティブタブの判定
  useEffect(() => {
    if (filteredTabs.length > 0 && pathname && companyId) {
      // 現在のパスから対象のタブルートを抽出
      let currentRoute = ''

      if (isPreview) {
        // プレビューページの場合: /preview/companies/{id}/ 以降を取得
        const previewBasePath = `/preview/companies/${companyId}`
        currentRoute =
          pathname === previewBasePath || pathname === previewBasePath + '/'
            ? ''
            : pathname.replace(previewBasePath + '/', '')
      } else {
        // 通常ページの場合
        currentRoute = pathname.replace(`/${companyId}`, '').replace(/^\//, '') || ''
      }

      // フィルタされたタブから該当するタブのインデックスを見つける
      const tabIndex = filteredTabs.findIndex((tab) => tab.route === currentRoute)

      if (tabIndex !== -1) {
        setActiveTab(tabIndex)
      } else {
        setActiveTab(0)
      }
    }
  }, [pathname, filteredTabs, companyId, isPreview])

  const tabLabels = useMemo(() => filteredTabs.map((tab) => tab.label), [filteredTabs])

  const previewTabs = useMemo(
    () => allTabs.filter((tab) => tab.label === 'ホーム' || tab.label === '会社概要'),
    [allTabs],
  )

  const previewCurrentRoute = useCallback((): string => {
    if (!pathname) return ''

    const path = pathname
    const id = params.id as string
    const basePath = `/preview/companies/${id}`
    return path === basePath || path === basePath + '/' ? '' : path.replace(basePath + '/', '')
  }, [pathname, params.id])

  return (
    <div className={clsx(styles.allContainer, styles.companyLayout, { preview: isPreviewPath() })}>
      <div
        id='main-container'
        className={clsx(styles.mainContainer, { companyPreview: isCompanyPreviewPath() })}
      >
        <Header />
        {companyCommonInfo?.coverImageUrl && (
          <div className={styles.coverWrapper}>
            <div className={styles.coverImage}>
              <img
                src={companyCommonInfo.coverImageUrl}
                alt={`${companyCommonInfo.name} カバー画像`}
                width={1200}
                height={300}
                style={{ objectFit: 'cover', borderRadius: '8px' }}
                loading='lazy'
              />
            </div>
          </div>
        )}
        {companyCommonInfo && (
          <CompanyCommonInfo
            companyData={companyCommonInfo as Company}
            used='header'
            showRoleModelFinderBanner={showRoleModelFinderBanner}
          />
        )}
        <div className={styles.companyTabs}>
          <CustomTabs
            tabs={tabLabels}
            activeIndex={activeTab}
            renderTab={(label, index, ref) => (
              <div
                key={index}
                ref={ref}
                className={clsx(styles.tabItem, { [styles.active]: index === activeTab })}
              >
                <Link
                  href={`/${companyId}${filteredTabs[index].route ? `/${filteredTabs[index].route}` : ''}`}
                  className={styles.tabItemLink}
                >
                  {label}
                </Link>
              </div>
            )}
          />
        </div>
        <div id='app' className={styles.appContainer}>
          {children}
        </div>
        <MessageAfterLogin memberId={getMemberId()} />
        {companyCommonInfo && (
          <CompanyCommonInfo
            companyData={companyCommonInfo as Company}
            used='footer'
            showRoleModelFinderBanner={showRoleModelFinderBanner}
          />
        )}
        <Footer />
        <NonMemberModal />
        {isPreviewPath() && (
          <div className={styles.previewFooter}>
            <div className={styles.previewButtonArea}>
              <p className={styles.previewTitle}>プレビューを選択</p>
              {previewTabs.map((item) => (
                <div
                  key={item.label}
                  className={clsx(styles.previewButton, {
                    [styles.disabled]: isDisabled && item.route === '',
                  })}
                >
                  {!(isDisabled && item.route === '') ? (
                    <Link
                      href={`/preview/companies/${params.id}/${item.route}`}
                      className={clsx(styles.previewLink, {
                        [styles.current]: previewCurrentRoute() === item.route,
                      })}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span
                      className={clsx(styles.previewLink, styles.disabled, {
                        [styles.current]: previewCurrentRoute() === item.route,
                      })}
                    >
                      {item.label}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <p className={styles.previewText}>プレビューではリンクはクリックできません。</p>
          </div>
        )}
      </div>
    </div>
  )
}
